import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton } from "@mui/material";
import { ReactNode, useState } from "react";
import { Typography } from "@platform/design-system";

const TEXT_SECONDARY_COLOR = "text.secondary";

interface MiniSectionProps {
  title: string;
  children: ReactNode;
  editContent?: ReactNode;
  defaultExpanded?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onEditingChange?: (isEditing: boolean) => void;
  saveDisabled?: boolean;
  /** Custom action rendered in the header instead of the edit/save controls. */
  headerAction?: ReactNode;
  /** When false, the section is always expanded with no chevron or toggle. */
  collapsible?: boolean;
}

const MiniSection = ({
  title,
  children,
  editContent,
  defaultExpanded = false,
  onEdit,
  onSave,
  onCancel,
  onEditingChange,
  saveDisabled = false,
  headerAction,
  collapsible = true,
}: MiniSectionProps): React.JSX.Element => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);

  const isExpanded = collapsible ? expanded : true;

  const updateIsEditing = (editing: boolean): void => {
    setIsEditing(editing);
    onEditingChange?.(editing);
  };

  const handleToggle = (): void => {
    setExpanded((prev) => !prev);
  };

  const handleEditClick = (): void => {
    if (onEdit) {
      // External edit handler (e.g., open drawer)
      onEdit();
    } else if (editContent) {
      // Inline editing mode
      updateIsEditing(true);
      setExpanded(true);
    }
  };

  const handleDiscardClick = (): void => {
    onCancel?.();
    updateIsEditing(false);
  };

  const handleSaveClick = (): void => {
    onSave?.();
    updateIsEditing(false);
  };

  return (
    <Box
      data-testid="mini-section"
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: collapsible ? "pointer" : "default",
          py: 1,
          px: 2,
        }}
        onClick={collapsible ? handleToggle : undefined}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {collapsible &&
            (expanded ? (
              <ExpandMoreIcon sx={{ color: TEXT_SECONDARY_COLOR, mr: 1 }} />
            ) : (
              <ChevronRightIcon sx={{ color: TEXT_SECONDARY_COLOR, mr: 1 }} />
            ))}
          <Typography variant="body-medium">
            <strong>{title}</strong>
          </Typography>
        </Box>
        {headerAction ? (
          // Stop clicks on the custom action from bubbling to the header's
          // toggle handler, matching the edit/save buttons below.
          <Box onClick={(e) => e.stopPropagation()}>{headerAction}</Box>
        ) : isEditing ? (
          <Box>
            <IconButton
              size="small"
              aria-label="discard"
              onClick={(e) => {
                e.stopPropagation();
                handleDiscardClick();
              }}
              sx={{ color: TEXT_SECONDARY_COLOR }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="save"
              disabled={saveDisabled}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveClick();
              }}
              sx={{ color: saveDisabled ? "action.disabled" : "#7B1FA2" }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <IconButton
            size="small"
            aria-label="edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick();
            }}
            sx={{ color: TEXT_SECONDARY_COLOR }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Expandable content */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ px: 2, py: 2, ml: 2 }}>
            {isEditing && editContent ? editContent : children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MiniSection;
